import { useState, useEffect, useMemo } from 'react';

interface Props {
  content: string;
}

const WORDS_PER_MINUTE = 200;
const RADIUS = 22;
const CIRCUMFERENCE = 2 * Math.PI * RADIUS;

function getWordCount(html: string): number {
  const div = document.createElement('div');
  div.innerHTML = html;
  const text = div.textContent || div.innerText || '';
  return text.split(/\s+/).filter(w => w.length > 0).length;
}

export default function ReadingTimeIndicator({ content }: Props) {
  const [progress, setProgress] = useState(0);

  const totalMinutes = useMemo(() => {
    const words = getWordCount(content);
    return Math.max(1, Math.round(words / WORDS_PER_MINUTE));
  }, [content]);

  useEffect(() => {
    const update = () => {
      const article = document.querySelector<HTMLElement>('article');
      if (!article) return;

      const articleTop = article.offsetTop;
      const articleHeight = article.offsetHeight;
      const readableHeight = articleHeight - window.innerHeight;
      const scrolled = window.scrollY - articleTop;
      const pct = readableHeight > 0
        ? Math.min(1, Math.max(0, scrolled / readableHeight))
        : 0;

      setProgress(pct);
    };

    window.addEventListener('scroll', update, { passive: true });
    update();
    return () => window.removeEventListener('scroll', update);
  }, []);

  const remaining = Math.max(0, Math.ceil(totalMinutes * (1 - progress)));
  const isDone = progress >= 0.98;
  const strokeDashoffset = CIRCUMFERENCE * (1 - progress);

  return (
    <div className="rounded-2xl border border-gray-100 dark:border-gray-700 bg-white dark:bg-gray-900 p-5">
      <p className="font-body text-xs font-bold uppercase tracking-widest text-gray-400 dark:text-gray-500 mb-5 flex items-center gap-2">
        <div className="w-4 h-4 flex items-center justify-center">
          <i className="ri-timer-2-line text-primary text-sm"></i>
        </div>
        Tempo de leitura
      </p>

      <div className="flex items-center gap-5">
        {/* Circular progress ring */}
        <div className="relative flex-shrink-0 w-14 h-14">
          <svg width="56" height="56" viewBox="0 0 56 56" className="-rotate-90">
            <circle
              cx="28" cy="28" r={RADIUS}
              fill="none"
              stroke="#e5e7eb"
              strokeWidth="4"
              className="dark:stroke-gray-700"
            />
            <circle
              cx="28" cy="28" r={RADIUS}
              fill="none"
              stroke={isDone ? '#22c55e' : '#18c7f4'}
              strokeWidth="4"
              strokeDasharray={CIRCUMFERENCE}
              strokeDashoffset={strokeDashoffset}
              strokeLinecap="round"
              style={{ transition: 'stroke-dashoffset 0.4s ease, stroke 0.3s ease' }}
            />
          </svg>
          <div className="absolute inset-0 flex items-center justify-center">
            {isDone ? (
              <div className="w-5 h-5 flex items-center justify-center">
                <i className="ri-check-line text-green-500 text-base"></i>
              </div>
            ) : (
              <span className="font-heading font-bold text-secondary dark:text-white text-lg leading-none">
                {remaining}
              </span>
            )}
          </div>
        </div>

        {/* Text */}
        <div>
          {isDone ? (
            <>
              <p className="font-body font-semibold text-green-600 dark:text-green-400 text-sm leading-tight">
                Artigo concluído!
              </p>
              <p className="font-body text-xs text-gray-400 dark:text-gray-500 mt-0.5">
                Obrigado por ler
              </p>
            </>
          ) : (
            <>
              <p className="font-heading font-bold text-secondary dark:text-white text-base leading-tight">
                {remaining === 1 ? '~1 minuto' : `~${remaining} min`}
              </p>
              <p className="font-body text-xs text-gray-400 dark:text-gray-500 mt-0.5">
                restantes
              </p>
            </>
          )}
        </div>
      </div>

      {/* Progress bar */}
      <div className="mt-4 h-1.5 bg-gray-100 dark:bg-gray-700 rounded-full overflow-hidden">
        <div
          className="h-full rounded-full transition-[width] duration-300 ease-out"
          style={{
            width: `${Math.round(progress * 100)}%`,
            background: isDone
              ? '#22c55e'
              : 'linear-gradient(90deg, #18c7f4, #384774)',
          }}
        />
      </div>
      <div className="flex justify-between mt-1.5">
        <span className="font-body text-xs text-gray-300 dark:text-gray-600">início</span>
        <span className="font-body text-xs text-gray-300 dark:text-gray-600">fim</span>
      </div>
    </div>
  );
}
